import React, { useState, useCallback, useContext, useRef, useMemo } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import { AuthContext, DataStateContext, DataActionsContext, ClientsContext } from "@/App";
import { toast } from "sonner";
import {
  Download, Upload, FileSpreadsheet, AlertTriangle,
  CheckCircle2, X, ShieldAlert, Info, RefreshCw, Plus, Loader2
} from "lucide-react";

// ── Match EXACTLY the export field order from server.py ──────────────────────
const TEMPLATE_COLS = [
  "item_description", "po_number", "part_number", "line_number", "revision",
  "client", "quantity", "unit", "vendor", "value_usd",
  "po_date", "due_date", "dispatch_due_date", "priority", "stage",
  "material_status", "tooling_status", "gauging_status",
  "material_spend", "tooling_spend", "gauging_spend",
  "manufacturing_status", "machine_used", "cycle_time",
  "qc_status",
  "dispatch_status", "carrier_name", "tracking_id",
  "is_blocked", "block_reason", "block_reason_code", "block_reason_subteam", "stage_comment", "is_qual",
  "is_rework", "return_reason",
];

// ── Valid enum values ──────────────────────────────────────────────────────────
const VALID_UNITS       = ["pcs", "kgs", "mtrs", "ltrs"];
const VALID_PRIORITIES  = ["Standard", "Expedite"];
const VALID_STAGES      = ["PO Received", "Procurement", "Manufacturing", "Quality Control", "Dispatch", "Completed"];
const VALID_PROC_STATUS = ["Pending", "Ordered", "Partially Received", "Received", "In Progress", "Complete"];
const VALID_MFG_STATUS  = ["Queued", "Processing", "Machining Complete"];
const VALID_QC_STATUS   = ["Pending Inspection", "Under Inspection", "Passed", "Failed", "Rework Required"];
const VALID_DISP_STATUS = ["Shipment Call Raised", "Pickup Awaited", "Negotiating Rates", "Shipped", ""];

// ── 5 example rows covering every stage + all new fields ─────────────────────
const EXAMPLE_ROWS = [
  ["Wireline Pressure Sub 4-1/2\"", "PO-2026-100", "RP-WPS-26-001", "001", "Rev A",
   "BH Wireline", "10", "pcs", "Ludhiana Precision Works", "25000",
   "2026-03-01", "2026-06-15", "", "Standard", "PO Received",
   "Pending", "Pending", "Pending", "0", "0", "0",
   "Queued", "", "", "Pending Inspection", "", "", "",
   "false", "", "", "", "", "false",
   "false", ""],

  ["Crossover Sub 3-1/2\" IF", "PO-2026-101", "RP-CS-26-002", "001", "Rev B",
   "SLB", "20", "pcs", "Patiala Forge & Machine", "18500",
   "2026-03-05", "2026-06-20", "", "Expedite", "Procurement",
   "In Progress", "Complete", "Pending", "1200", "850", "0",
   "Queued", "", "", "Pending Inspection", "", "", "",
   "true", "Material Import / Logistics Delay — customs hold", "RM-05", "Material", "Material on order from Patiala", "false",
   "false", ""],

  ["Mud Pump Valve Seat", "PO-2026-102", "RP-MV-26-003", "001", "Rev A",
   "Weatherford", "50", "pcs", "Mohali Chrome Works", "32000",
   "2026-02-20", "2026-05-25", "", "Standard", "Manufacturing",
   "Complete", "Complete", "Complete", "3500", "1200", "600",
   "Machining Complete", "CNC-3", "6.5 hrs", "Pending Inspection", "", "", "",
   "false", "", "", "", "Sent back from QC — surface finish out of tolerance", "false",
   "true", "Surface finish Ra value exceeded 1.6 micron — re-machining required"],

  ["Flow Diverter Sub 5\"", "PO-2026-103", "RP-FD-26-004", "001", "Rev C",
   "BH FPS", "5", "pcs", "Amritsar Engineering", "47000",
   "2026-02-10", "2026-05-10", "", "Expedite", "Quality Control",
   "Complete", "Complete", "Complete", "5200", "1800", "900",
   "Machining Complete", "VMC-1", "12 hrs", "Under Inspection", "", "", "",
   "false", "", "", "", "Surface finish check in progress", "true",
   "false", ""],

  ["Drill Collar 6-3/4\"", "PO-2026-104", "RP-DC-26-005", "002", "Rev A",
   "BH Wireline", "3", "pcs", "Jalandhar Steel & Forge", "61000",
   "2026-01-15", "2026-04-30", "2026-04-28", "Standard", "Dispatch",
   "Complete", "Complete", "Complete", "8400", "2100", "1500",
   "Machining Complete", "CNC-7", "18 hrs", "Passed",
   "Shipped", "Blue Dart Express", "BD9902847651",
   "false", "", "", "", "Delivered to BH Wireline Baroda depot", "false",
   "false", ""],
];

function generateTemplateCSV() {
  const escape = (v) => (String(v).includes(",") || String(v).includes('"') ? `"${String(v).replace(/"/g, '""')}"` : String(v));
  const lines = [TEMPLATE_COLS.join(",")];
  EXAMPLE_ROWS.forEach(row => lines.push(row.map(escape).join(",")));
  return lines.join("\n");
}

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };
  const parseRow = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQuotes = !inQuotes; }
      else if (ch === ',' && !inQuotes) { result.push(current.trim()); current = ""; }
      else { current += ch; }
    }
    result.push(current.trim());
    return result;
  };
  const headers = parseRow(lines[0]);
  const rows = lines.slice(1).map(l => parseRow(l));
  return { headers, rows };
}

function validateRow(headers, values, validClients) {
  const errors = [];
  const get = (col) => { const i = headers.indexOf(col); return i >= 0 ? (values[i] || "").trim() : ""; };

  if (!get("item_description")) errors.push("Missing item_description");
  if (!get("po_number"))        errors.push("Missing po_number");
  if (!get("due_date"))         errors.push("Missing due_date");

  const client = get("client");
  if (client && validClients.length > 0 && !validClients.includes(client))
    errors.push(`Unknown client "${client}" — add it in Settings or use: ${validClients.join(", ")}`);

  const qty = get("quantity");
  if (qty && isNaN(Number(qty))) errors.push("quantity must be a number");
  const val = get("value_usd");
  if (val && isNaN(Number(val))) errors.push("value_usd must be a number");
  ["material_spend", "tooling_spend", "gauging_spend"].forEach(f => {
    const v = get(f);
    if (v && isNaN(Number(v))) errors.push(`${f} must be a number`);
  });

  const unit = get("unit");
  if (unit && !VALID_UNITS.includes(unit)) errors.push(`unit must be one of: ${VALID_UNITS.join(", ")}`);
  const priority = get("priority");
  if (priority && !VALID_PRIORITIES.includes(priority)) errors.push(`priority must be Standard or Expedite`);
  const stage = get("stage");
  if (stage && !VALID_STAGES.includes(stage)) errors.push(`Invalid stage value`);
  const ms = get("material_status");
  if (ms && !VALID_PROC_STATUS.includes(ms)) errors.push(`material_status must be: ${VALID_PROC_STATUS.join(", ")}`);
  const ts = get("tooling_status");
  if (ts && !VALID_PROC_STATUS.includes(ts)) errors.push(`tooling_status must be: ${VALID_PROC_STATUS.join(", ")}`);
  const gs = get("gauging_status");
  if (gs && !VALID_PROC_STATUS.includes(gs)) errors.push(`gauging_status must be: ${VALID_PROC_STATUS.join(", ")}`);
  const mfg = get("manufacturing_status");
  if (mfg && !VALID_MFG_STATUS.includes(mfg)) errors.push(`manufacturing_status must be: ${VALID_MFG_STATUS.join(", ")}`);
  const qc = get("qc_status");
  if (qc && !VALID_QC_STATUS.includes(qc)) errors.push(`qc_status must be: ${VALID_QC_STATUS.join(", ")}`);
  const ds = get("dispatch_status");
  if (ds && !VALID_DISP_STATUS.includes(ds)) errors.push(`dispatch_status must be: ${VALID_DISP_STATUS.filter(Boolean).join(", ")}`);
  ["po_date", "due_date", "dispatch_due_date"].forEach(f => {
    const v = get(f);
    if (v && !/^\d{4}-\d{2}-\d{2}$/.test(v)) errors.push(`${f} must be YYYY-MM-DD`);
  });

  return errors;
}

function rowToItem(headers, values) {
  const get = (col, def = "") => {
    const i = headers.indexOf(col);
    return i >= 0 && values[i]?.trim() ? values[i].trim() : def;
  };
  return {
    item_description:    get("item_description"),
    po_number:           get("po_number"),
    part_number:         get("part_number"),
    line_number:         get("line_number", "001"),
    revision:            get("revision"),
    client:              get("client", ""),
    quantity:            Number(get("quantity", "0")) || 0,
    unit:                get("unit", "pcs"),
    vendor:              get("vendor"),
    value_usd:           Number(get("value_usd", "0")) || 0,
    po_date:             get("po_date", new Date().toISOString().split("T")[0]),
    due_date:            get("due_date"),
    dispatch_due_date:   get("dispatch_due_date"),
    priority:            get("priority", "Standard"),
    stage:               get("stage", "PO Received"),
    material_status:     get("material_status", "Pending"),
    tooling_status:      get("tooling_status", "Pending"),
    gauging_status:      get("gauging_status", "Pending"),
    material_spend:      Number(get("material_spend", "0")) || 0,
    tooling_spend:       Number(get("tooling_spend", "0")) || 0,
    gauging_spend:       Number(get("gauging_spend", "0")) || 0,
    manufacturing_status: get("manufacturing_status", "Queued"),
    machine_used:        get("machine_used"),
    cycle_time:          get("cycle_time"),
    qc_status:           get("qc_status", "Pending Inspection"),
    dispatch_status:     get("dispatch_status"),
    carrier_name:        get("carrier_name"),
    tracking_id:         get("tracking_id"),
    is_blocked:          get("is_blocked", "false").toLowerCase() === "true",
    block_reason:        get("block_reason"),
    block_reason_code:   get("block_reason_code"),
    block_reason_subteam: get("block_reason_subteam"),
    stage_comment:       get("stage_comment"),
    is_qual:             get("is_qual", "false").toLowerCase() === "true",
    is_rework:           get("is_rework", "false").toLowerCase() === "true",
    return_reason:       get("return_reason"),
  };
}

const FIELD_DOCS = [
  { label: "priority",             values: VALID_PRIORITIES,  note: "Default: Standard" },
  { label: "unit",                 values: VALID_UNITS,        note: "Default: pcs" },
  { label: "stage",                values: VALID_STAGES,       note: "Default: PO Received" },
  { label: "material_status / tooling_status / gauging_status", values: VALID_PROC_STATUS, note: "Default: Pending" },
  { label: "manufacturing_status", values: VALID_MFG_STATUS,   note: "Default: Queued" },
  { label: "qc_status",            values: VALID_QC_STATUS,    note: "Default: Pending Inspection" },
  { label: "dispatch_status",      values: VALID_DISP_STATUS.filter(Boolean), note: "Optional — leave blank" },
  { label: "is_blocked / is_qual / is_rework", values: ["true", "false"],  note: "Default: false" },
  { label: "block_reason_code",    values: ["e.g. MFG-03, RM-05"], note: "Required when is_blocked=true. Must match the item's stage — see /api/reason-codes. Invalid codes are dropped." },
  { label: "block_reason_subteam", values: ["Material", "Tooling", "Gauging", "Quality Control", "Quality Assurance"], note: "Only for Procurement & Quality Control stages" },
  { label: "return_reason",         values: ["text"],           note: "Optional — reason item was sent back from QC" },
  { label: "po_date / due_date / dispatch_due_date", values: ["YYYY-MM-DD"], note: "e.g. 2026-06-30" },
  { label: "material_spend / tooling_spend / gauging_spend", values: ["numeric (USD)"], note: "e.g. 1250.00" },
  { label: "machine_used",         values: ["text"],           note: "e.g. CNC-3, VMC-1, Lathe-2" },
  { label: "cycle_time",           values: ["text"],           note: "e.g. 4.5 hrs, 2 days" },
  { label: "carrier_name / tracking_id", values: ["text"],    note: "Only when dispatch_status = Shipped" },
];

export default function ImportModal({ onClose }) {
  const { user }                 = useContext(AuthContext);
  const { items } = useContext(DataStateContext);
  const { bulkCreateItems, bulkUpsertItems, refreshData } = useContext(DataActionsContext);
  const { clients }              = useContext(ClientsContext);
  const validClientNames         = clients.map(c => c.name);

  const [tab, setTab]            = useState("template");
  const [parsedData, setParsedData] = useState(null);
  const [updateMode, setUpdateMode] = useState(false);
  const [importing, setImporting]   = useState(false);
  const [dragOver, setDragOver]     = useState(false);
  const fileRef                     = useRef(null);
  const isAdmin                     = user?.role === "Admin";

  // Build a lookup map from existing items: "po_number||line_number" → true
  const existingKeysMap = useMemo(() => {
    const map = new Map();
    items.forEach(item => {
      const key = `${item.po_number}||${item.line_number || "001"}`;
      map.set(key, item.id);
    });
    return map;
  }, [items]);

  // When update mode is on, categorise each valid row as CREATE or UPDATE
  const categorizedRows = useMemo(() => {
    if (!parsedData) return null;
    return parsedData.rows.map(row => {
      const get = (col) => { const i = parsedData.headers.indexOf(col); return i >= 0 ? (row.values[i] || "").trim() : ""; };
      const po   = get("po_number");
      const line = get("line_number") || "001";
      const key  = `${po}||${line}`;
      const action = (updateMode && existingKeysMap.has(key)) ? "UPDATE" : "CREATE";
      return { ...row, action, matchKey: key };
    });
  }, [parsedData, updateMode, existingKeysMap]);

  const downloadTemplate = () => {
    const csv  = generateTemplateCSV();
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = "rocklink_import_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const handleFile = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const { headers, rows } = parseCSV(e.target.result);
      const validated = rows.map((values, idx) => ({
        idx: idx + 1,
        values,
        errors: validateRow(headers, values, validClientNames),
        valid:  validateRow(headers, values, validClientNames).length === 0,
      }));
      setParsedData({ headers, rows: validated,
        totalCount:  validated.length,
        errorCount:  validated.filter(r => !r.valid).length,
        validCount:  validated.filter(r => r.valid).length,
      });
    };
    reader.readAsText(file);
  }, [validClientNames]);

  const handleDrop = useCallback((e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file?.name.endsWith(".csv")) handleFile(file);
  }, [handleFile]);

  const CHUNK_SIZE = 500;
  const [importProgress, setImportProgress] = useState(null); // "Batch 2 / 3"

  const handleImport = async () => {
    if (!categorizedRows) return;
    const validRows = categorizedRows.filter(r => r.valid);
    const itemsData = validRows.map(r => rowToItem(parsedData.headers, r.values));
    setImporting(true);
    setImportProgress(null);

    // Split into 500-item chunks to stay within server limits
    const chunks = [];
    for (let i = 0; i < itemsData.length; i += CHUNK_SIZE) {
      chunks.push(itemsData.slice(i, i + CHUNK_SIZE));
    }

    try {
      let totalCreated = 0;
      let totalUpdated = 0;
      for (let i = 0; i < chunks.length; i++) {
        if (chunks.length > 1) setImportProgress(`Batch ${i + 1} / ${chunks.length}`);
        if (updateMode) {
          const res = await bulkUpsertItems(chunks[i], true /* silent */);
          totalCreated += res?.created ?? 0;
          totalUpdated += res?.updated ?? 0;
        } else {
          const res = await bulkCreateItems(chunks[i], true /* silent */);
          totalCreated += res?.count ?? chunks[i].length;
        }
      }
      await refreshData();
      if (updateMode) {
        toast.success(`Processed ${validCount} row${validCount !== 1 ? "s" : ""} — ${totalCreated} created, ${totalUpdated} updated`);
      } else {
        toast.success(`Imported ${totalCreated} item${totalCreated !== 1 ? "s" : ""}`);
      }
      onClose();
    } catch { /* toast handled in context */ }
    setImporting(false);
    setImportProgress(null);
  };

  // Derived counts for update mode display
  const createCount  = categorizedRows?.filter(r => r.valid && r.action === "CREATE").length ?? 0;
  const updateCount  = categorizedRows?.filter(r => r.valid && r.action === "UPDATE").length ?? 0;
  const validCount   = parsedData?.validCount ?? 0;
  const errorCount   = parsedData?.errorCount ?? 0;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-2xl max-h-[88vh] overflow-y-auto"
        data-testid="import-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-orange-400" /> Import Line Items
          </DialogTitle>
          <DialogDescription>
            Download the template CSV ({TEMPLATE_COLS.length} columns · 5 example rows), fill in your data, then upload.
          </DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex border-b border-[hsl(var(--border))] mb-4">
          {[
            { key: "template", icon: Download, label: "Download Template" },
            { key: "upload",   icon: Upload,   label: "Upload CSV"        },
          ].map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                tab === key
                  ? "border-orange-500 text-orange-400"
                  : "border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
              }`}
              data-testid={`tab-${key}`}
            >
              <Icon className="w-4 h-4 inline mr-1.5" />{label}
            </button>
          ))}
        </div>

        {/* ── Tab A: Template ────────────────────────────────────────────── */}
        {tab === "template" && (
          <div className="space-y-5" data-testid="template-tab-content">
            <div className="flex items-start gap-4 p-4 rounded-lg bg-orange-500/5 border border-orange-500/20">
              <Info className="w-5 h-5 text-orange-400 shrink-0 mt-0.5" />
              <div className="text-sm text-[hsl(var(--muted-foreground))]">
                <p className="font-semibold text-[hsl(var(--foreground))] mb-1">Template includes 5 example rows</p>
                <p>Each row covers a different production stage (PO Received → Dispatch), demonstrating all available fields including procurement sub-team statuses, machining data, dispatch tracking, and spend values.</p>
              </div>
            </div>

            <button
              onClick={downloadTemplate}
              className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-orange-600 text-white text-sm font-semibold hover:bg-orange-500 transition-colors"
              data-testid="download-template-btn"
            >
              <Download className="w-4 h-4" /> Download Template CSV
              <span className="ml-1 text-xs font-normal opacity-80">({TEMPLATE_COLS.length} columns · 5 example rows)</span>
            </button>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2">All {TEMPLATE_COLS.length} columns</p>
              <div className="flex flex-wrap gap-1.5">
                {TEMPLATE_COLS.map(col => (
                  <span key={col} className="text-[10px] font-mono-data px-2 py-0.5 rounded bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] border border-[hsl(var(--border))]">{col}</span>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2">Valid values per field</p>
              <div className="space-y-0 text-xs rounded-lg border border-[hsl(var(--border))] overflow-hidden">
                <div className="flex items-start gap-3 px-3 py-2.5 border-b border-[hsl(var(--border))]/50 bg-[hsl(var(--muted))]/20">
                  <span className="font-mono-data font-bold text-[hsl(var(--foreground))] w-52 shrink-0 pt-0.5">client</span>
                  <div className="flex flex-wrap gap-1">
                    {validClientNames.map(n => (
                      <span key={n} className="px-1.5 py-0.5 rounded bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]">{n}</span>
                    ))}
                    <span className="px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-400 italic">or any new client created in Settings</span>
                  </div>
                </div>
                {FIELD_DOCS.map(({ label, values, note }) => (
                  <div key={label} className="flex items-start gap-3 px-3 py-2 border-b border-[hsl(var(--border))]/50 last:border-0">
                    <span className="font-mono-data font-bold text-[hsl(var(--foreground))] w-52 shrink-0 pt-0.5 leading-snug">{label}</span>
                    <div>
                      <div className="flex flex-wrap gap-1 mb-0.5">
                        {values.map(v => <span key={v} className="px-1.5 py-0.5 rounded bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]">{v}</span>)}
                      </div>
                      {note && <span className="text-[hsl(var(--muted-foreground))] italic">{note}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Tab B: Upload ──────────────────────────────────────────────── */}
        {tab === "upload" && (
          <div className="space-y-4" data-testid="upload-tab-content">
            {!isAdmin && (
              <div className="flex items-center gap-2 p-3 rounded-md bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="admin-warning">
                <ShieldAlert className="w-4 h-4 shrink-0" /> Only Admins can import data.
              </div>
            )}

            {/* ── Update Mode Toggle ─────────────────────────────────────── */}
            <div
              className={`flex items-start gap-3 p-3.5 rounded-lg border transition-colors ${
                updateMode
                  ? "border-amber-500/40 bg-amber-500/5"
                  : "border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20"
              }`}
              data-testid="update-mode-row"
            >
              <button
                role="switch"
                aria-checked={updateMode}
                onClick={() => setUpdateMode(v => !v)}
                className={`relative w-9 h-5 rounded-full shrink-0 transition-colors mt-0.5 ${updateMode ? "bg-amber-500" : "bg-[hsl(var(--muted-foreground))]/30"}`}
                data-testid="update-mode-toggle"
              >
                <span
                  className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform"
                  style={{ transform: updateMode ? "translateX(16px)" : "translateX(0)" }}
                />
              </button>
              <div>
                <p className="text-sm font-semibold text-[hsl(var(--foreground))] flex items-center gap-1.5">
                  <RefreshCw className="w-3.5 h-3.5 text-amber-500" />
                  Update existing items
                </p>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 leading-snug">
                  When <strong>on</strong>: rows whose <code className="font-mono-data text-[10px] bg-[hsl(var(--muted))] px-1 rounded">po_number + line_number</code> match an existing item will <strong>update</strong> it instead of creating a duplicate.
                  When <strong>off</strong>: every valid row is always created as a new item.
                </p>
              </div>
            </div>

            {/* Drop zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => isAdmin && fileRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                dragOver
                  ? "border-orange-500 bg-orange-500/5"
                  : "border-[hsl(var(--border))] hover:border-[hsl(var(--muted-foreground))]"
              } ${!isAdmin ? "opacity-50 !cursor-not-allowed" : ""}`}
              data-testid="drop-zone"
            >
              <Upload className="w-8 h-8 mx-auto mb-2 text-[hsl(var(--muted-foreground))]" />
              <p className="text-sm text-[hsl(var(--muted-foreground))]">Drag & drop a CSV file, or click to browse</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]/60 mt-1">.csv files only</p>
              <input
                ref={fileRef}
                type="file"
                accept=".csv"
                className="hidden"
                disabled={!isAdmin}
                onChange={(e) => handleFile(e.target.files[0])}
                data-testid="file-input"
              />
            </div>

            {/* Preview */}
            {categorizedRows && parsedData && (
              <div className="space-y-3" data-testid="import-preview">
                {/* Summary bar */}
                <div className="flex items-center gap-4 text-sm flex-wrap">
                  <span className="font-mono-data font-bold text-[hsl(var(--foreground))]">{parsedData.totalCount} rows</span>
                  {errorCount > 0 && (
                    <span className="flex items-center gap-1 text-red-400 font-medium">
                      <AlertTriangle className="w-3.5 h-3.5" />{errorCount} errors
                    </span>
                  )}
                  {updateMode ? (
                    <>
                      {createCount > 0 && (
                        <span className="flex items-center gap-1 font-medium" style={{ color: "#22c55e" }}>
                          <Plus className="w-3.5 h-3.5" />{createCount} new
                        </span>
                      )}
                      {updateCount > 0 && (
                        <span className="flex items-center gap-1 text-amber-500 font-medium">
                          <RefreshCw className="w-3.5 h-3.5" />{updateCount} will update existing
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="flex items-center gap-1 text-green-500 font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5" />{validCount} valid
                    </span>
                  )}
                </div>

                {/* Table */}
                <div className="border border-[hsl(var(--border))] rounded-lg overflow-hidden max-h-[280px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 sticky top-0">
                        {["#", "Description", "PO", "Line", "Client", "Stage", "Status"].map(h => (
                          <th key={h} className="px-3 py-2 text-left font-semibold text-[hsl(var(--muted-foreground))]">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {categorizedRows.map((row) => {
                        const g = (col) => { const i = parsedData.headers.indexOf(col); return i >= 0 ? (row.values[i] || "") : ""; };
                        const isUpdate = updateMode && row.valid && row.action === "UPDATE";
                        const isNew    = updateMode && row.valid && row.action === "CREATE";
                        return (
                          <tr
                            key={row.idx}
                            className={`border-b border-[hsl(var(--border))]/50 ${!row.valid ? "bg-red-500/5" : isUpdate ? "bg-amber-500/5" : ""}`}
                            data-testid={`import-row-${row.idx}`}
                          >
                            <td className="px-3 py-2 font-mono-data text-[hsl(var(--muted-foreground))]">{row.idx}</td>
                            <td className="px-3 py-2 max-w-[160px] truncate">{g("item_description")}</td>
                            <td className="px-3 py-2 font-mono-data">{g("po_number")}</td>
                            <td className="px-3 py-2 font-mono-data">{g("line_number") || "001"}</td>
                            <td className="px-3 py-2">{g("client")}</td>
                            <td className="px-3 py-2">{g("stage")}</td>
                            <td className="px-3 py-2">
                              {!row.valid ? (
                                <div className="flex items-start gap-1 text-red-400">
                                  <X className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                  <span className="leading-tight" title={row.errors.join(" | ")}>{row.errors[0]}</span>
                                </div>
                              ) : isUpdate ? (
                                <span className="flex items-center gap-1 font-bold text-amber-500 uppercase tracking-wider text-[10px]">
                                  <RefreshCw className="w-3 h-3" /> UPDATE
                                </span>
                              ) : isNew ? (
                                <span className="flex items-center gap-1 font-bold text-green-500 uppercase tracking-wider text-[10px]">
                                  <Plus className="w-3 h-3" /> NEW
                                </span>
                              ) : (
                                <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {errorCount > 0 && (
                  <p className="text-xs text-amber-500 flex items-center gap-1.5 bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    Rows with errors will be skipped — only valid rows will be processed.
                  </p>
                )}

                {/* Confirm button */}
                <button
                  onClick={handleImport}
                  disabled={!isAdmin || validCount === 0 || importing}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-white text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    updateMode ? "bg-amber-600 hover:bg-amber-500" : "bg-orange-600 hover:bg-orange-500"
                  }`}
                  data-testid="confirm-import-btn"
                >
                  {importing
                    ? <><Loader2 className="w-4 h-4 animate-spin" />{importProgress ? `Importing… ${importProgress}` : "Processing..."}</>
                    : updateMode
                      ? <><RefreshCw className="w-4 h-4" />{`Process ${validCount} row${validCount !== 1 ? "s" : ""} — ${createCount} new, ${updateCount} update${errorCount > 0 ? ` (${errorCount} skipped)` : ""}`}</>
                      : <><CheckCircle2 className="w-4 h-4" />{`Import ${validCount} item${validCount !== 1 ? "s" : ""}${errorCount > 0 ? ` (${errorCount} skipped)` : ""}${validCount > CHUNK_SIZE ? ` · ${Math.ceil(validCount / CHUNK_SIZE)} batches` : ""}`}</>
                  }
                </button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
